// Crash Course Funnel — admin-only widget showing lead-magnet captures →
// drip lesson progression → paid signup attribution.
//
// Drops onto the AdminFunnel page so admins see all conversion data
// (visitor→user, onboarding, crash-course) on one screen.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, TrendingUp, XCircle, CheckCircle2, Mail } from "lucide-react";

const DAY_LABELS = {
  0: "Welcome (Day 0)",
  1: "Lesson 1 — Finding Deals",
  2: "Lesson 2 — Negotiation",
  3: "Lesson 3 — Contracts",
  4: "Lesson 4 — Title & Closing",
  5: "Lesson 5 — Closing Day",
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-lg p-4"
      data-testid={`crash-course-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-500">
        <Icon size={12} style={{ color: accent }} /> {label}
      </div>
      <div className="text-2xl font-bold text-zinc-900 mt-1">{value}</div>
    </div>
  );
}

export default function CrashCourseFunnelCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/lead-magnet-funnel")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const id = setInterval(() => {
      api.get("/admin/lead-magnet-funnel").then((r) => setData(r.data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 mt-8" data-testid="crash-course-funnel-loading">
        <div className="h-4 w-48 bg-zinc-200 rounded animate-pulse" />
      </div>
    );
  }

  const maxCount = Math.max(
    1,
    ...Object.values(data.captures_by_lesson_day || {}),
  );
  // Rebuild day-by-day rows so days with 0 still render (visual completeness).
  const rows = [0, 1, 2, 3, 4, 5].map((d) => ({
    day: d,
    captures: (data.captures_by_lesson_day || {})[d] || 0,
    signups: (data.signups_by_drip_day || {})[d] || 0,
  }));

  return (
    <section className="mt-8" data-testid="crash-course-funnel-card">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-lg font-bold text-zinc-900">5-Day Crash Course Funnel</h3>
        <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
          Live
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat icon={FileText} label="Captures" value={data.total_captures} accent="#0F172A" />
        <Stat icon={CheckCircle2} label="Completed drip" value={data.completed_full_drip} accent="#10B981" />
        <Stat icon={XCircle} label="Opted out" value={data.opted_out} accent="#F43F5E" />
        <Stat
          icon={TrendingUp}
          label="Conv rate"
          value={`${data.conversion_rate_pct}%`}
          accent="#10B981"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="crash-course-day-table">
          <thead>
            <tr className="bg-zinc-50 text-left">
              <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Lesson</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide w-1/2">Currently at this day</th>
              <th className="px-4 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide text-right">Signups</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pct = Math.round((r.captures / maxCount) * 100);
              return (
                <tr key={r.day} className="border-t border-zinc-100" data-testid={`crash-course-row-day-${r.day}`}>
                  <td className="px-4 py-3 text-sm text-zinc-700 font-medium whitespace-nowrap">
                    {DAY_LABELS[r.day]}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-sm font-bold text-zinc-900 w-8 text-right">{r.captures}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.signups > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                        +{r.signups} signed up
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.top_utm_sources?.length > 0 && (
        <div className="mt-5 bg-white border border-zinc-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-zinc-500 mb-2">
            <Mail size={12} /> Top UTM sources (all signups)
          </div>
          <div className="flex flex-wrap gap-2">
            {data.top_utm_sources.map((s) => (
              <span key={s.source} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700">
                <span className="font-semibold">{s.source}</span>
                <span className="text-zinc-500">· {s.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
